import { IObstetricaLabResultsGroup } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { Locale } from "@/Common/Models/Localization/Locale";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { OntarioObstetricalFileClickableTextContext } from "@/Common/Models/UI/ClickableTextContext";
import { HeightUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { IObstetricalFilePageProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/ObstetricalFilePage";
import { OntarioObstetricalFilePage, OntarioObstetricalFilePageState } from "@/Patient/Components/MedicalNote/Obstetric/Form/Ontario/OntarioObstetricalFilePage";
import { BloodPressureField } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/BloodPressureField";
import { ObstetricalFileField } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/ObstetricalFileField";
import { OntarioObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Ontario/OntarioObstetricalFileFieldLimits";
import { MedicalCalculator } from "@/Patient/Helpers/MedicalCalculator";
import { BloodPressure } from "@/Patient/Models/MedicalSummary/BloodPressure";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";
import { HeightUnitComponent } from "../../../Common/Subcomponent/HeightUnitComponent";
import { WeightUnitComponent } from "../../../Common/Subcomponent/WeightUnitComponent";
import { OntarioObstetricalFileAdditionalInvestigations } from "./Subcomponent/OntarioObstetricalFileAdditionalInvestigations";
import { OntarioObstetricalFileExamAsIndicated } from "./Subcomponent/OntarioObstetricalFileExamAsIndicated";
import { OntarioObstetricalFileInitialLabInvestigations } from "./Subcomponent/OntarioObstetricalFileInitialLabInvestigations";
import { OntarioObstetricalFileLabWidget } from "./Subcomponent/OntarioObstetricalFileLabWidget";
import { OntarioObstetricalFileNoScreeningTest } from "./Subcomponent/OntarioObstetricalFileNoScreeningTest";
import { OntarioObstetricalFilePrenatalGeneticInvestigations } from "./Subcomponent/OntarioObstetricalFilePrenatalGeneticInvestigations";
import { OntarioObstetricalFileSecondThirdTrimesterLabInvestigation } from "./Subcomponent/OntarioObstetricalFileSecondThirdTrimesterLabInvestigation";
import { OntarioObstetricalFileUltrasound } from "./Subcomponent/OntarioObstetricalFileUltrasound";

class OntarioObstetricalFilePageTwoState extends OntarioObstetricalFilePageState {
    public vitalSign: ObstetricalFileVitalSign;
    public obstetricalFileLabObservations: IObstetricaLabResultsGroup[];
}

class OntarioObstetricalFileOpr2 extends OntarioObstetricalFilePage<OntarioObstetricalFilePageTwoState> {
    private readonly componentId = "ontario-obstetrical-file-OPR2";

    protected initState(props: IObstetricalFilePageProps<OntarioObstetricalFileForm>): void {
        this.mutateState(
            s => {
                s.form = new OntarioObstetricalFileForm(props.form);

                s.vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                    obstetricalFileMedicalSummary: props.obstetricalFileMedicalSummary,
                    emrTemplateId: s.form.Opr2.VitalSignEmrTemplateId,
                    consultationDate: props.encounter.ConsultationDate,
                    obstetricalFileContributionDate: props.contributionCreationDate,
                    skipLoadLastHour:
                        s.form.Opr3.SubsequentVisits?.any()
                        && !!s.form.Opr3.SubsequentVisits[0].EmrTemplateId
                        && props.obstetricalFileMedicalSummary.getVitalSignBy(s.form.Opr3.SubsequentVisits[0].EmrTemplateId, props.encounter.ConsultationDate) != null,
                    heightUnit: s.form.Opr2.HeightUnit
                });

                if (s.form.isVitalSignReusedInOPR3())
                    s.form.Opr3.SubsequentVisits[0].EmrTemplateId = s.vitalSign?.emrTemplateId;

                s.form.Opr2.VitalSignEmrTemplateId = s.vitalSign?.emrTemplateId;
            },
            () => {
                if (this.state.form.Opr2.VitalSignEmrTemplateId)
                    this.handleObstetricalFileVitalSignLink(this.state.form.Opr2.VitalSignEmrTemplateId);

                this.props.fetchLabObservations?.().then(
                    labs => this.mutateState(s => s.obstetricalFileLabObservations = labs)
                );
            }
        );
    }

    protected renderPageContent(): JSX.Element {
        return (
            <div id={this.componentId} className="inner-border">
                {this.state.obstetricalFileLabObservations?.any() && (
                    <OntarioObstetricalFileLabWidget
                        obstetricalFileLabObservations={this.state.obstetricalFileLabObservations}
                        finalEDB={this.props.form.Common.FinalEstimatedDateOfBirth}
                        mutateForm={(mutation, then) => this.mutateState(mutation, then)}
                        isInEditMode={!this.props.readonly}
                    />
                )}

                {this.renderPersonalInfoAndObstetricalCountersSection()}
                <div className="row border border-top flex-display">
                    <div className="col-md-4">
                        {this.renderExamSection()}
                        <OntarioObstetricalFileExamAsIndicated {...this.getSubcomponentBaseProps()} />
                    </div>
                    <OntarioObstetricalFileInitialLabInvestigations {...this.getSubcomponentBaseProps()} />
                    <OntarioObstetricalFileSecondThirdTrimesterLabInvestigation {...this.getSubcomponentBaseProps()} />
                </div>
                <div className="row border border-top">
                    <div className="col-md-4">
                        {this.renderCommentExam()}
                        {this.renderPapSection()}
                    </div>
                    <div className="col-md-8 border border-left">
                        {this.renderAdditionalInvestigationsSection()}
                    </div>
                </div>
                <OntarioObstetricalFilePrenatalGeneticInvestigations {...this.getSubcomponentBaseProps()} />
                <OntarioObstetricalFileNoScreeningTest
                    {...this.getSubcomponentBaseProps()}
                    consultationDateAtStartOfObstetricalFile={this.props.consultationDateAtStartOfObstetricalFile}
                />
                <OntarioObstetricalFileUltrasound
                    {...this.getSubcomponentBaseProps()}
                    patientDateOfBirth={this.props.patient.IndividualResponse.DateOfBirth}
                />
                {this.renderFooter()}
            </div>
        );
    }

    protected async onVitalSignUpdated(): Promise<void> {
        let updatedVitalSign = await super.handleVitalSignUpdate(
            this.state.vitalSign,
            this.state.form.Opr2.VitalSignEmrTemplateId
        );

        this.mutateState(
            s => {
                s.vitalSign.updateValueFromMedicalItemVitalSign(updatedVitalSign, null, this.state.form.Opr2.HeightUnit);
                s.form.Opr2.VitalSignEmrTemplateId = updatedVitalSign?.EMRTemplateId;
            },
            () => this.handleObstetricalFileVitalSignLink(updatedVitalSign?.EMRTemplateId)
        );
    }

    private renderPersonalInfoAndObstetricalCountersSection(): JSX.Element {
        return (
            <div className="section">
                <div className="row row-form">
                    {this.renderLastName("col-md-4")}
                    {this.renderFirstName("col-md-4")}
                </div>
                <div className="row row-form">
                    {this.renderPlannedBirthAttendant("col-md-4")}
                    {this.renderNewBornFields("col-md-8")}
                </div>
                <div className="row row-form">
                    <div className="col-xl-12">
                        <div className="flex-display flex-wrap flex-start">
                            {this.renderObstetricalCounters("margin-md-left margin-md-right flex-grow-1")}
                            {this.renderFinalEstimatedBirthDate("OPR2-FinalEdb", "margin-md-left margin-md-right")}
                            {this.renderFamilyPhysicianOrPrimaryCareProvider("margin-md-left margin-md-right flex-grow-1 max-width-50percent")}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderExamSection(): JSX.Element {
        const commonData = this.state.form.Common;
        const shouldDisplayHeightUnit = (this.props.readonly && this.state.vitalSign?.heightInMeters) || !this.props.readonly;
        const shouldDisplayWeightUnit = (this.props.readonly && commonData.PrePregnancyWeight) || !this.props.readonly;
        const isLatestMedicalItemInEmrTemplate = this.props.obstetricalFileMedicalSummary.isLatestMedicalItemInEmrTemplate(this.state.vitalSign);

        return (
            <div className="section opr2-physical-exam">
                {this.renderSectionTitleRow("PhysicalExamLabel")}
                <div className="row row-form">
                    <div className="col-md-6">
                        <ObstetricalFileField
                            label="HeightLabel"
                            field={this.vitalSignControlRenderer.renderTextbox({
                                value: this.state.vitalSign?.getHeightIn(this.state.form.Opr2.HeightUnit)?.toString(),
                                isReadOnlyOverride: !isLatestMedicalItemInEmrTemplate,
                                onChange: value => this.handleHeightChange(value && parseFloat(value)),
                                maxLength: 7,
                                isNumeric: true,
                                className: `${this.state.vitalSign?.emrIsInvalid ? "invalid-vital-sign" : ""}`
                            })}
                            className="flex-column"
                            unit={shouldDisplayHeightUnit && (
                                <HeightUnitComponent
                                    units={[HeightUnit.Centimeter, HeightUnit.Inch]}
                                    selectedUnit={this.state.form.Opr2.HeightUnit}
                                    onUnitChange={unit => this.mutateModelState(s => {
                                        s.form.Opr2.HeightUnit = unit;
                                        s.vitalSign.heightUnit = unit;
                                    })}
                                    readonly={this.props.readonly}
                                    acceptableLocales={[Locale.en]}
                                />
                            )}
                            inlineFlexValue
                        />
                    </div>
                    <div className="col-md-6">
                        <ObstetricalFileField
                            label="PrePregnancyWeightAbbrevLabel"
                            field={this.controlRenderer.renderAutoCompleteTextbox({
                                value: commonData.ConvertedPrePregnancyWeight?.toString(),
                                autoCompleteItems: this.getWeightBeforePregnancyDropdownOptions(this.state.form.Common.PrePregnancyWeightUnit),
                                onChange: value => this.handlePrePregnancyWeightChange(value),
                                onAutoCompleteSelected: code => this.handlePrePregnancyWeightChange(code),
                                maxLength: 8,
                                isNumeric: true,
                                disableMatchAutoSorting: true,
                                listWidth: "150px"
                            })}
                            className="flex-column"
                            unit={shouldDisplayWeightUnit && (
                                <WeightUnitComponent
                                    units={[WeightUnit.Kilogram, WeightUnit.Pound]}
                                    selectedUnit={this.state.form.Common.PrePregnancyWeightUnit}
                                    onUnitChange={unit => this.mutateModelStateWithFormUpdate(s => s.form.Common.PrePregnancyWeightUnit = unit)}
                                    readonly={this.props.readonly}
                                    acceptableLocales={[Locale.en]}
                                />
                            )}
                            inlineFlexValue
                        />
                    </div>
                </div>
                <div className="row row-form">
                    <div className="col-md-6 flex-display">
                        {this.renderField(
                            "BloodPressureAbbrevLabel",
                            <BloodPressureField
                                readonly={this.props.readonly || !isLatestMedicalItemInEmrTemplate}
                                bloodPressure={this.state.vitalSign?.bloodPressure}
                                isInvalid={this.state.vitalSign.emrIsInvalid}
                                onBloodPressureChange={(bloodPressure: BloodPressure) => this.mutateModelState(s => s.vitalSign.bloodPressure = bloodPressure)}
                                onBlur={() => this.onVitalSignUpdated()}
                            />,
                            "flex-column",
                            " "
                        )}
                    </div>
                    <div className="row row-form">
                        <div className="col-md-6 flex-display">
                            {this.renderField(
                                "BloodPressureAbbrevLabel",
                                <BloodPressureField
                                    readonly={this.props.readonly || !isLatestMedicalItemInEmrTemplate}
                                    bloodPressure={this.state.vitalSign?.bloodPressure}
                                    isInvalid={this.state.vitalSign.emrIsInvalid}
                                    onBloodPressureChange={(bloodPressure: BloodPressure) => this.mutateModelState(s => s.vitalSign.bloodPressure = bloodPressure)}
                                    onBlur={() => this.onVitalSignUpdated()}
                                />,
                                "flex-column",
                                " "
                            )}
                        </div>
                        <div className="col-md-6">
                            {this.renderField(
                                "PrePregnancyBodyMassIndexAbbrevLabel",
                                this.controlRenderer.renderTextbox({
                                    value: commonData.PrePregnancyBodyMassIndex?.toString(),
                                    onChange: value => this.mutateState(s => s.form.Common.PrePregnancyBodyMassIndex = value && Number(value)),
                                    maxLength: 8,
                                    isNumeric: true
                                }),
                                "flex-column",
                                " "
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public renderCommentExam(): JSX.Element {
        const id = "Exam_comments";
        return (
            <div className="section">
                <div className="row row-form">
                    <div className="col-md-12">
                        {this.renderSectionTitleRow("ObstetricalFile_ExamCommentsLabel")}
                        {this.controlRenderer.renderTextarea({
                            value: this.state.form.Opr2.ExamComments,
                            onChange: value => this.mutateState(s => s.form.Opr2.ExamComments = value),
                            id: id,
                            maxLength: OntarioObstetricalFileFieldLimits.Page2.ExamComments.MaxChars,
                            maxRows: OntarioObstetricalFileFieldLimits.Page2.ExamComments.MaxRows,
                            clickableTextOptions: this.getClickableTextOptions(id, OntarioObstetricalFileClickableTextContext.Opr2_PhysicalExamComment, true)
                        })}
                    </div>
                </div>
            </div>
        );
    }

    public renderPapSection(): JSX.Element {
        return (
            <div className="section border border-top">
                <div className="row row-form margin-sm-top">
                    {this.renderField(
                        "LastPapLabel",
                        this.controlRenderer.renderDateSelector({
                            value: this.state.form.Opr2.LastPapDate,
                            onChange: value => this.mutateModelStateWithFormUpdate(s => s.form.Opr2.LastPapDate = value),
                            id: "Exam_LastPapDate",
                            className: "width-100"
                        }),
                        "col-md-6"
                    )}
                    {this.renderField(
                        "ResultLabel",
                        this.controlRenderer.renderAutoCompleteTextbox({
                            value: this.state.form.Opr2.LastPapResult,
                            autoCompleteItems: this.getPapResultOptions(),
                            onChange: value => this.mutateState(s => s.form.Opr2.LastPapResult = value),
                            onAutoCompleteSelected: (code, _, value) => this.mutateState(s => s.form.Opr2.LastPapResult = value),
                            maxLength: OntarioObstetricalFileFieldLimits.Page2.LastPapTestResult.MaxChars,
                            disableMatchAutoSorting: true
                        }),
                        "col-md-6"
                    )}
                </div>
            </div>
        );
    }

    private renderAdditionalInvestigationsSection(): JSX.Element {
        return (
            <div className="section">
                {this.renderSectionTitleRow("ObstetricalFile_AdditionalInvestigationsLabel", "margin-none-bottom")}
                <OntarioObstetricalFileAdditionalInvestigations {...this.getSubcomponentBaseProps()} />
            </div>
        );
    }

    private renderFooter(): JSX.Element {
        return (
            <div className="row row-form margin-sm-top">
                <div className="col-md-12">
                    <div className="col-md-4">
                        {this.controlRenderer.renderCheckbox(
                            "ObstetricalFile_GeneticScreeningResultReviewedLabel",
                            this.state.form.Opr2.IsGeneticScreeningResultReviewed,
                            value => this.mutateModelStateWithFormUpdate(s => s.form.Opr2.IsGeneticScreeningResultReviewed = value),
                            "margin-right-15px margin-top-3px",
                            "text-bold"
                        )}
                    </div>
                    <div className="col-md-4 medicalhistory-inline-textbox">
                        <div className="padding-xs-right"><p className="text-bold">{this.getString("ObstetricalFile_Approx22wksLabel")}:</p></div>
                        {this.controlRenderer.renderCheckbox(
                            "ObstetricalFile_CopyOfOpr2ToHospitalLabel",
                            this.state.form.Opr2.IsPage1AndPage2CopiedToHospital,
                            value => this.mutateModelStateWithFormUpdate(s => s.form.Opr2.IsPage1AndPage2CopiedToHospital = value),
                            "margin-right-15px margin-top-3px"
                        )}
                    </div>
                    <div className="col-md-4">
                        {this.controlRenderer.renderCheckbox(
                            "ObstetricalFile_AndOrPtClientLabel",
                            this.state.form.Opr2.IsPage1AndPage2CopiedToClient,
                            value => this.mutateModelStateWithFormUpdate(s => s.form.Opr2.IsPage1AndPage2CopiedToClient = value),
                            "margin-right-15px margin-top-3px"
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private getPapResultOptions(): SearchableItemModelT<string>[] {
        return [
            new SearchableItemModelT(this.getString("NormalLabel"), "PapResult_Normal"),
            new SearchableItemModelT(this.getString("AbnormalLabel"), "PapResult_Abnormal"),
            new SearchableItemModelT(this.getString("QuestionableLabel"), "PapResult_Questionable"),
            new SearchableItemModelT(this.getString("AscusAcronymLabel"), "PapResult_Ascus"),
            new SearchableItemModelT(this.getString("LsilAcronymLabel"), "PapResult_LSIL"),
            new SearchableItemModelT(this.getString("HsilAcronymLabel"), "PapResult_HSIL")
        ];
    }

    private handlePrePregnancyWeightChange(value: string): void {
        this.mutateState(s => {
            s.form.Common.ConvertedPrePregnancyWeight = value && parseFloat(value);

            if (s.form.Common.PrePregnancyWeight && s.vitalSign.heightInMeters) {
                let bodyMassIndex = MedicalCalculator.calculateBodyMassIndex(s.form.Common.PrePregnancyWeight, s.vitalSign?.heightInMeters)?.toFixed(2);
                s.form.Common.PrePregnancyBodyMassIndex = bodyMassIndex ? parseFloat(bodyMassIndex) : null;
            }
        });
    }

    private handleHeightChange(value: number): void {
        this.mutateState(s => {
            s.vitalSign.heightUnit = this.state.form.Opr2.HeightUnit;
            s.vitalSign.height = value;

            if (s.vitalSign.heightInMeters && s.form.Common.PrePregnancyWeight) {
                let bodyMassIndex = MedicalCalculator.calculateBodyMassIndex(s.form.Common.PrePregnancyWeight, s.vitalSign?.heightInMeters)?.toFixed(2);
                s.form.Common.PrePregnancyBodyMassIndex = bodyMassIndex ? Number(bodyMassIndex) : null;
            }
        });
    }
}

export { OntarioObstetricalFileOpr2, OntarioObstetricalFilePageTwoState };
